import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, inject, signal, ViewChild, ElementRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ServiceCompanyService } from '../../../services/service-company.service';
import {
  ServiceCompanyAggregateDto,
  ServiceCompanyMudCheckPropertiesDto,
  MudCheckPdfExtractionResultDto
} from '@models/service-company-dto.interface';

type MudCheckSection = 'density' | 'rheology' | 'electricalStability' | 'fluidLoss' | 'chemistries' | 'solidsAnalysis';

// Fields cleared when a section is removed via its delete button
const SECTION_FIELDS: Record<MudCheckSection, (keyof ServiceCompanyMudCheckPropertiesDto)[]> = {
  density: ['density', 'densityTemperature'],
  rheology: ['rheologyTemperature', 'rpm600', 'rpm300', 'rpm200', 'rpm100', 'rpm6', 'rpm3', 'gel10Sec', 'gel10Min', 'plasticViscosity', 'yieldPoint'],
  electricalStability: ['elecStability'],
  fluidLoss: ['hthp', 'htpTemperature', 'cakeHTHP'],
  chemistries: ['alkalinity', 'chlorides', 'calcium', 'cacl2', 'naClSoluble', 'naClInsoluble', 'excessLime', 'waterPhaseSalinity'],
  solidsAnalysis: ['napWaterRatio', 'lgsPercent', 'corrSolids', 'asg']
};

const SECTION_ICONS: Record<MudCheckSection, string> = {
  density: 'straighten',
  rheology: 'show_chart',
  electricalStability: 'bolt',
  fluidLoss: 'water_drop',
  chemistries: 'science',
  solidsAnalysis: 'grain'
};

const SECTION_LABELS: Record<MudCheckSection, string> = {
  density: 'Density',
  rheology: 'Rheology',
  electricalStability: 'Electrical Stability',
  fluidLoss: 'Fluid Loss',
  chemistries: 'Chemistries',
  solidsAnalysis: 'Solids Analysis'
};

const RHEOLOGY_REQUIRED_FIELDS: { field: keyof ServiceCompanyMudCheckPropertiesDto; label: string }[] = [
  { field: 'rheologyTemperature', label: 'Temp (F)' },
  { field: 'rpm600', label: '600' },
  { field: 'rpm300', label: '300' },
  { field: 'rpm200', label: '200' },
  { field: 'rpm100', label: '100' },
  { field: 'rpm6', label: '6' },
  { field: 'rpm3', label: '3' },
  { field: 'gel10Sec', label: '10s' },
  { field: 'gel10Min', label: '10m' },
  { field: 'plasticViscosity', label: 'PV' },
  { field: 'yieldPoint', label: 'YP' }
];

@Component({
  selector: 'app-service-company',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './service-company.component.html',
  styleUrl: './service-company.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceCompanyComponent implements OnChanges {
  @Input() jobId: number | null = null;
  @Input() isEditable: boolean = true;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private serviceCompanyService = inject(ServiceCompanyService);

  // State signals
  data = signal<ServiceCompanyAggregateDto | null>(null);
  isLoading = signal(false);
  isSaving = signal(false);
  isUploading = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);

  // Visibility of each section on the (single) Mud Check Properties record for this job.
  // Fields stay hidden until the user clicks "+ Add" for that section, or a PDF upload
  // populates values for it.
  visibleSections = signal<Set<MudCheckSection>>(new Set());

  constructor() {
    // Refresh when Mud Check Properties records change elsewhere (e.g. a PDF is
    // uploaded/deleted from the Attachments tab) - both tabs are instantiated
    // eagerly and keep their own local copy of the aggregate, so this tab won't
    // otherwise see the change until the whole page is reloaded.
    this.serviceCompanyService.onMudCheckPropertiesChanged()
      .pipe(takeUntilDestroyed())
      .subscribe(changedJobId => {
        if (changedJobId === this.jobId) {
          this.loadData();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    // `jobId` is a plain @Input (not a signal), and the parent JobDetailsComponent
    // reuses this component instance across job navigations (it reacts to route
    // param changes rather than being destroyed/recreated) - so ngOnChanges is the
    // only reliable place to detect a job switch and reload. Reset local state
    // immediately so a previous job's values never flash while the new job loads.
    if (changes['jobId']) {
      this.data.set(null);
      this.visibleSections.set(new Set());
      this.errorMessage.set(null);
      this.successMessage.set(null);

      if (this.jobId && this.jobId > 0) {
        this.loadData();
      }
    }
  }

  loadData() {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.serviceCompanyService.getServiceCompanyData(this.jobId).subscribe({
      next: (payload) => {
        this.data.set(payload);
        this.recomputeVisibleSections();
        this.isLoading.set(false);
      },
      error: (err) => {
        this.errorMessage.set(err?.error?.error || 'Failed to load service company data');
        this.isLoading.set(false);
      }
    });
  }

  saveData() {
    if (!this.data()) return;

    const validationMessage = this.validateBeforeSave();
    if (validationMessage) {
      this.errorMessage.set(validationMessage);
      this.successMessage.set(null);
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    this.serviceCompanyService.saveServiceCompanyData(this.jobId, this.data()!).subscribe({
      next: (result) => {
        this.data.set(result);
        this.recomputeVisibleSections();
        this.isSaving.set(false);
        this.successMessage.set('Service Company Data saved successfully.');
      },
      error: (err) => {
        this.errorMessage.set(err?.error?.error || 'Failed to save service company data');
        this.isSaving.set(false);
      }
    });
  }

  // ========== Single Mud Check Properties record ==========

  /** Returns the one Mud Check Properties record for this job, if it exists. */
  get record(): ServiceCompanyMudCheckPropertiesDto | undefined {
    return this.data()?.mudCheckProperties[0];
  }

  private getOrCreateRecord(): ServiceCompanyMudCheckPropertiesDto {
    const current = this.data();
    if (!current) throw new Error('Data not loaded');
    if (!current.mudCheckProperties.length) {
      current.mudCheckProperties.push({ id: 0 });
    }
    return current.mudCheckProperties[0];
  }

  private validateBeforeSave(): string | null {
    const record = this.record;

    if (this.isSectionVisible('rheology') && record) {
      const missingFields = RHEOLOGY_REQUIRED_FIELDS
        .filter(item => this.isMissingValue(record[item.field]))
        .map(item => item.label);

      if (missingFields.length) {
        return `Rheology is incomplete. Fill all Rheology values before saving. Missing: ${missingFields.join(', ')}.`;
      }
    }

    return null;
  }

  private isMissingValue(value: unknown): boolean {
    return value === undefined || value === null || value === '';
  }

  private recomputeVisibleSections() {
    const record = this.record;
    if (!record) {
      this.visibleSections.set(new Set());
      return;
    }
    const visible = new Set<MudCheckSection>();
    for (const section of Object.keys(SECTION_FIELDS) as MudCheckSection[]) {
      const hasValue = SECTION_FIELDS[section].some(field => record[field] !== undefined && record[field] !== null);
      if (hasValue) visible.add(section);
    }
    this.visibleSections.set(visible);
  }

  isSectionVisible(section: MudCheckSection): boolean {
    return this.visibleSections().has(section);
  }

  sectionIcon(section: MudCheckSection): string {
    return SECTION_ICONS[section];
  }

  sectionLabel(section: MudCheckSection): string {
    return SECTION_LABELS[section];
  }

  addSection(section: MudCheckSection) {
    this.getOrCreateRecord();
    this.data.set({ ...this.data()! });
    this.visibleSections.update(set => new Set(set).add(section));
  }

  removeSection(section: MudCheckSection) {
    const record = this.record;
    if (record) {
      for (const field of SECTION_FIELDS[section]) {
        (record as any)[field] = undefined;
      }
      this.data.set({ ...this.data()! });
    }
    this.visibleSections.update(set => {
      const next = new Set(set);
      next.delete(section);
      return next;
    });
  }

  // ========== PDF Upload ==========
  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  onPdfFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;

    const file = input.files[0];
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      this.errorMessage.set('Please select a PDF file.');
      input.value = '';
      return;
    }

    if (!this.jobId) return;

    this.isUploading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    const existingId = this.record?.id ?? 0;

    this.serviceCompanyService.uploadMudCheckPropertiesPdf(this.jobId, existingId, file).subscribe({
      next: (result: MudCheckPdfExtractionResultDto) => {
        this.isUploading.set(false);
        this.successMessage.set(
          result.fieldsExtractedCount > 0
            ? `Extracted ${result.fieldsExtractedCount} field(s) from the PDF. Please review and verify all values before saving.`
            : 'Could not automatically extract data from this PDF. Please enter values manually and verify.'
        );
        input.value = '';
        // The backend has already persisted the record (created/updated), so reload
        // to get the authoritative state and reveal any sections with extracted data.
        this.loadData();
      },
      error: (err) => {
        this.isUploading.set(false);
        this.errorMessage.set(err?.error?.error || 'Failed to upload PDF. Please try again.');
        input.value = '';
      }
    });
  }

  rereadFromPdf() {
    const record = this.record;
    if (!record || !this.jobId || !record.id || !record.pdfFileName) {
      this.errorMessage.set('No PDF file attached to this record or record not yet saved.');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.serviceCompanyService.rereadMudCheckPropertiesPdf(this.jobId, record.id).subscribe({
      next: (result) => {
        this.isLoading.set(false);
        this.successMessage.set(result.message);
        this.loadData();
      },
      error: (err) => {
        this.errorMessage.set(err?.error?.error || 'Failed to re-read PDF. Please try again.');
        this.isLoading.set(false);
      }
    });
  }
}

